/**
 * Product FAQ drawer — Ritual.com-style trigger rows + slide-in panel, with
 * a Jolie-style bottom tab bar inside each open panel so people can jump
 * between the 5 topics without closing and reopening the drawer.
 *
 * One panel component doubles as a desktop side-panel and a mobile
 * full-screen overlay: it's `position:fixed; right:0; max-width:460px`,
 * which is wider than any phone viewport, so on mobile it fills the
 * whole screen automatically — no separate mobile markup needed.
 */
(function () {
  function initFaqDrawer(root) {
    if (!root || root.dataset.faqInit === 'true') return;
    root.dataset.faqInit = 'true';

    var triggers = root.querySelectorAll('[data-faq-trigger]');
    var panels = root.querySelectorAll('[data-faq-panel]');
    var tabs = root.querySelectorAll('[data-faq-tab]');
    var backdrop = root.querySelector('[data-faq-backdrop]');
    var lastTrigger = null;

    function getPanel(key) {
      for (var i = 0; i < panels.length; i++) {
        if (panels[i].getAttribute('data-faq-panel') === key) return panels[i];
      }
      return null;
    }

    function getTrigger(key) {
      for (var i = 0; i < triggers.length; i++) {
        if (triggers[i].getAttribute('data-faq-trigger') === key) return triggers[i];
      }
      return null;
    }

    function updateTabs(key) {
      tabs.forEach(function (tab) {
        var isActive = tab.getAttribute('data-faq-tab') === key;
        tab.classList.toggle('is-active', isActive);
        if (isActive) {
          tab.setAttribute('aria-current', 'page');
        } else {
          tab.removeAttribute('aria-current');
        }
      });
    }

    function closeAll() {
      panels.forEach(function (panel) {
        panel.classList.remove('is-active');
      });
      triggers.forEach(function (t) {
        t.setAttribute('aria-expanded', 'false');
      });
      updateTabs(null);
      root.classList.remove('is-open');
      document.body.classList.remove('faq-drawer-open');
      if (lastTrigger) {
        lastTrigger.focus({ preventScroll: true });
        lastTrigger = null;
      }
      document.removeEventListener('keydown', onKeydown);
    }

    function onKeydown(e) {
      if (e.key === 'Escape') closeAll();
    }

    function openPanel(key, trigger, opts) {
      var panel = getPanel(key);
      if (!panel) return;
      var focusPanel = !opts || opts.focusPanel !== false;
      // "instant" is used when switching panels via the bottom tab bar while
      // the drawer is already open: the panel itself (the fixed side-panel /
      // full-screen sheet) should stay put — only the content inside swaps —
      // instead of sliding out and back in like a fresh open.
      var instant = !!(opts && opts.instant);

      if (instant) {
        panels.forEach(function (p) {
          p.style.transition = 'none';
        });
      }

      panels.forEach(function (p) {
        p.classList.toggle('is-active', p === panel);
      });

      if (instant) {
        // Force a reflow so the transform/visibility change above is
        // committed with no transition, then remove the override
        // immediately (synchronously — no rAF/timeout needed: the reflow
        // read is what "locks in" the jump) so the drawer still animates
        // normally next time, e.g. when it's closed.
        void panel.offsetWidth;
        panels.forEach(function (p) {
          p.style.transition = '';
        });
      }

      triggers.forEach(function (t) {
        t.setAttribute('aria-expanded', t.getAttribute('data-faq-trigger') === key ? 'true' : 'false');
      });
      updateTabs(key);

      root.classList.add('is-open');
      document.body.classList.add('faq-drawer-open');
      lastTrigger = trigger || getTrigger(key) || lastTrigger;
      document.addEventListener('keydown', onKeydown);

      var scroller = panel.querySelector('.faq-drawer__panel-scroll');
      if (scroller) scroller.scrollTop = 0;

      if (focusPanel) {
        var closeBtn = panel.querySelector('[data-faq-close]');
        if (closeBtn) closeBtn.focus({ preventScroll: true });
      }
    }

    triggers.forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        var key = trigger.getAttribute('data-faq-trigger');
        var isOpen = trigger.getAttribute('aria-expanded') === 'true';
        if (isOpen) {
          closeAll();
        } else {
          openPanel(key, trigger);
        }
      });
    });

    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        var key = tab.getAttribute('data-faq-tab');
        if (tab.classList.contains('is-active')) return;
        openPanel(key, null, { focusPanel: false, instant: true });
      });
    });

    panels.forEach(function (panel) {
      var closeBtn = panel.querySelector('[data-faq-close]');
      if (closeBtn) closeBtn.addEventListener('click', closeAll);
    });

    if (backdrop) backdrop.addEventListener('click', closeAll);
  }

  function init() {
    document.querySelectorAll('[data-faq-drawer]').forEach(initFaqDrawer);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var drawer = e.target.querySelector && e.target.querySelector('[data-faq-drawer]');
    if (drawer) initFaqDrawer(drawer);
  });
})();